import React from 'react';
import { Progress } from './ui/progress';
import { cn } from '../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Trophy, Target, Flame } from 'lucide-react';

// Custom Monk Mode image
export const MONK_IMAGE_URL = "https://customer-assets.emergentagent.com/job_fc70e8a7-91d2-4d7f-977d-e07f378252ba/artifacts/zlmzll26_Monk%20Mode.png";

// Monk-themed level progression system
// Each level has a unique monk evolution and title
export const MONK_LEVELS = [
  { min: 1, max: 2, title: 'Novice', emoji: '🧘', color: 'from-slate-400 to-slate-500', bgColor: 'bg-slate-100', textColor: 'text-[#1A1A1A]' },
  { min: 3, max: 4, title: 'Initiate', emoji: '📿', color: 'from-amber-400 to-amber-500', bgColor: 'bg-[#FDF0EC]', textColor: 'text-[#B04A32]' },
  { min: 5, max: 6, title: 'Disciple', emoji: '⚔️', color: 'from-emerald-400 to-emerald-600', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700' },
  { min: 7, max: 9, title: 'Monk', emoji: '🕯️', color: 'from-orange-400 to-orange-600', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  { min: 10, max: 14, title: 'Sage', emoji: '🌿', color: 'from-teal-400 to-teal-600', bgColor: 'bg-teal-100', textColor: 'text-teal-700' },
  { min: 15, max: 19, title: 'Master', emoji: '🏔️', color: 'from-indigo-400 to-indigo-600', bgColor: 'bg-indigo-100', textColor: 'text-indigo-700' },
  { min: 20, max: 29, title: 'Enlightened', emoji: '👁️', color: 'from-violet-400 to-violet-600', bgColor: 'bg-[#F4F3EE]', textColor: 'text-[#1A1A1A]' },
  { min: 30, max: 999, title: 'Ascended', emoji: '🌟', color: 'from-yellow-400 to-orange-500', bgColor: 'bg-gradient-to-r from-yellow-100 to-orange-100', textColor: 'text-orange-700' },
];

export const getMonkLevel = (level) => {
  return MONK_LEVELS.find(m => level >= m.min && level <= m.max) || MONK_LEVELS[0];
};

// Compact badge for leaderboards and lists
export const MonkLevelBadge = ({ level, size = 'sm', showTooltip = true }) => {
  const monkInfo = getMonkLevel(level);
  
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5 gap-1',
    sm: 'text-sm px-2 py-1 gap-1.5',
    md: 'text-base px-3 py-1.5 gap-2',
    lg: 'text-lg px-4 py-2 gap-2',
  };

  const badge = (
    <div 
      className={cn(
        'inline-flex items-center rounded-full font-medium transition-all',
        monkInfo.bgColor,
        monkInfo.textColor,
        sizeClasses[size],
        'hover:scale-105 cursor-default'
      )}
    >
      <span>{monkInfo.emoji}</span>
      <span className="font-semibold">Lv.{level}</span>
    </div>
  );

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent side="top" className="text-center">
          <p className="font-bold">{monkInfo.title}</p>
          <p className="text-xs text-muted-foreground">Level {level}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Full gamification card with monk theme
export default function MonkGamification({ stats, className }) {
  if (!stats) return null;

  const monkInfo = getMonkLevel(stats.level);
  const progress = (stats.xp % 500) / 5; // Progress within current level (0-100%)
  const xpInLevel = stats.xp % 500;
  const xpForNextLevel = 500;

  return (
    <div className={cn(
      "relative overflow-hidden rounded-2xl shadow-xl border",
      "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900",
      "border-slate-700/50",
      className
    )}>
      {/* Decorative zen pattern */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
        <svg viewBox="0 0 100 100" className="w-full h-full text-coral">
          <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="50" cy="50" r="30" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="50" cy="50" r="20" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="50" cy="50" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
        </svg>
      </div>

      <div className="relative p-5">
        {/* Header with Monk Avatar */}
        <div className="flex items-center gap-4 mb-4">
          {/* Monk Avatar Circle */}
          <div className={cn(
            "relative w-16 h-16 rounded-full flex items-center justify-center",
            "bg-gradient-to-br shadow-lg ring-2 ring-white/20",
            monkInfo.color
          )}>
            <span className="text-3xl">{monkInfo.emoji}</span>
            {/* Level indicator */}
            <div className="absolute -bottom-1 -right-1 bg-slate-900 rounded-full px-2 py-0.5 text-xs font-bold text-white border border-slate-600">
              {stats.level}
            </div>
          </div>
          
          {/* Title and Rank */}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white">{monkInfo.title}</h3>
            <p className="text-[#9E8778] text-sm">
              {stats.xp.toLocaleString()} total XP earned
            </p>
          </div>
        </div>

        {/* XP Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-[#9E8778]">Progress to Level {stats.level + 1}</span>
            <span className="text-white font-mono">{xpInLevel} / {xpForNextLevel} XP</span>
          </div>
          <div className="relative h-3 bg-slate-700/50 rounded-full overflow-hidden">
            <div 
              className={cn("h-full rounded-full transition-all duration-500 bg-gradient-to-r", monkInfo.color)}
              style={{ width: `${progress}%` }}
            />
            {/* Milestone markers */}
            <div className="absolute inset-0 flex justify-between px-1">
              {[25, 50, 75].map(mark => (
                <div 
                  key={mark}
                  className={cn(
                    "w-0.5 h-full",
                    progress >= mark ? "bg-white/30" : "bg-slate-600"
                  )}
                  style={{ marginLeft: `${mark - 1}%` }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-800/60 rounded-xl p-3 text-center border border-slate-700/50">
            <div className="text-2xl mb-1">🎯</div>
            <div className="text-2xl font-bold text-white">{stats.completed}</div>
            <div className="text-xs text-[#9E8778]">Goals Won</div>
          </div>
          <div className="bg-slate-800/60 rounded-xl p-3 text-center border border-slate-700/50">
            <div className="text-2xl mb-1">💚</div>
            <div className="text-2xl font-bold text-emerald-400">${stats.total_saved}</div>
            <div className="text-xs text-[#9E8778]">Saved</div>
          </div>
          <div className="bg-slate-800/60 rounded-xl p-3 text-center border border-slate-700/50">
            <div className="text-2xl mb-1">🔥</div>
            <div className="text-2xl font-bold text-coral">{stats.active}</div>
            <div className="text-xs text-[#9E8778]">Active</div>
          </div>
        </div>

        {/* Next Level Preview */}
        {stats.level < 30 && (
          <div className="mt-4 pt-3 border-t border-slate-700/50">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#9E8778]">Next rank:</span>
              <div className="flex items-center gap-2">
                {(() => {
                  // Find the next rank that's different from current
                  const currentRank = monkInfo.title;
                  const nextRankInfo = MONK_LEVELS.find(m => m.min > stats.level);
                  if (nextRankInfo && nextRankInfo.title !== currentRank) {
                    return (
                      <>
                        <span className="text-lg">{nextRankInfo.emoji}</span>
                        <span className="text-white font-medium">{nextRankInfo.title}</span>
                        <span className="text-[#5F5F5F] text-xs">(Lv.{nextRankInfo.min})</span>
                      </>
                    );
                  }
                  return (
                    <>
                      <span className="text-lg">{getMonkLevel(stats.level + 1).emoji}</span>
                      <span className="text-white font-medium">Level {stats.level + 1}</span>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Mini version for navbar or compact displays
export const MonkLevelMini = ({ level, xp, nextLevelXp }) => {
  const monkInfo = getMonkLevel(level);
  const progress = (xp % 500) / 5;

  return (
    <div className="flex items-center gap-2 bg-slate-800 rounded-full px-3 py-1.5">
      <span className="text-lg">{monkInfo.emoji}</span>
      <div className="flex flex-col">
        <span className="text-xs font-bold text-white">Lv.{level}</span>
        <div className="w-12 h-1 bg-slate-700 rounded-full overflow-hidden">
          <div 
            className={cn("h-full bg-gradient-to-r", monkInfo.color)}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// Compact horizontal bar for Dashboard (Arena)
export const MonkGamificationCompact = ({ stats, className }) => {
  if (!stats) return null;

  const monkInfo = getMonkLevel(stats.level);
  const progress = (stats.xp % 500) / 5;
  const xpInLevel = stats.xp % 500;
  const xpForNextLevel = 500;

  // Find next rank
  const nextRankInfo = MONK_LEVELS.find(m => m.min > stats.level);

  return (
    <div className={cn(
      "rounded-xl border bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-slate-700/50 p-3",
      className
    )}>
      <div className="flex items-center gap-4">
        {/* Monk Avatar - Compact */}
        <div className={cn(
          "relative w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0",
          "bg-gradient-to-br shadow-lg",
          monkInfo.color
        )}>
          <span className="text-2xl">{monkInfo.emoji}</span>
          <div className="absolute -bottom-0.5 -right-0.5 bg-slate-900 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white border border-slate-700">
            {stats.level}
          </div>
        </div>

        {/* Level Info & Progress */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="font-bold text-white text-sm">{monkInfo.title}</span>
              <span className="text-[#9E8778] text-xs">{stats.xp} XP</span>
            </div>
            {nextRankInfo && (
              <span className="text-[#5F5F5F] text-xs hidden sm:inline">
                Next: {nextRankInfo.emoji} {nextRankInfo.title}
              </span>
            )}
          </div>
          
          {/* Progress Bar */}
          <div className="relative h-2 bg-slate-700/50 rounded-full overflow-hidden">
            <div 
              className={cn("h-full rounded-full transition-all duration-500 bg-gradient-to-r", monkInfo.color)}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-[#5F5F5F] mt-0.5">
            <span>Lv.{stats.level}</span>
            <span>{xpInLevel}/{xpForNextLevel} XP</span>
            <span>Lv.{stats.level + 1}</span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="hidden md:flex items-center gap-3 pl-3 border-l border-slate-700">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <Target className="w-3 h-3 text-emerald-400" />
              <span className="text-white font-bold text-sm">{stats.completed}</span>
            </div>
            <span className="text-[10px] text-[#5F5F5F]">Won</span>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <Trophy className="w-3 h-3 text-coral" />
              <span className="text-white font-bold text-sm">${stats.total_saved}</span>
            </div>
            <span className="text-[10px] text-[#5F5F5F]">Saved</span>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <Flame className="w-3 h-3 text-orange-400" />
              <span className="text-white font-bold text-sm">{stats.active}</span>
            </div>
            <span className="text-[10px] text-[#5F5F5F]">Active</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Backward compatibility exports (mapped from old Panda names)
export const PANDA_LEVELS = MONK_LEVELS;
export const getPandaLevel = getMonkLevel;
export const PandaLevelBadge = MonkLevelBadge;
export const PandaLevelMini = MonkLevelMini;
export const PandaGamificationCompact = MonkGamificationCompact;
